/*
 *
 * AdminPage
 *
 * Gated shell for the React superadmin section (`/admin`). This replaces the
 * rails server-rendered HTML superadmin panel — see the parity plan in
 * programs/2026-06-22-superadmin-parity-rails-retirement/.
 *
 * Two independent gates protect this surface:
 *
 *   1. Feature flag `react_superadmin_enabled` (PostHog). Ships dark — the
 *      section only renders once the flag is on for the user, so the route can
 *      land in production OFF and flip ON when parity is confirmed.
 *
 *   2. Role guard. Only `super_admin` users may enter. The pre-session race
 *      (where `permission_role` is still falsy because /api/v2/session hasn't
 *      resolved) renders a Loader rather than redirecting, so a real
 *      super_admin reloading on /admin is never bounced to /channels.
 *
 * IMPORTANT: this client-side guard is UX only. The backend enforces
 * super_admin on every /api/v3/admin/* endpoint (403 for non-admins), which is
 * the actual security boundary. The flag/role checks here just decide what to
 * render; they do not protect data.
 */

import React from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useSelector } from "react-redux";
import { Helmet, HelmetProvider } from "react-helmet-async";

import Loader from "components/Loader";
import { useFeatureFlag } from "utils/posthog";
import { selectIsSuperAdmin, selectRoleResolved } from "./selectors";
import { Wrapper } from "./styledComponents";

const AdminPage = () => {
  const superadminEnabled = useFeatureFlag("react_superadmin_enabled");
  const isSuperAdmin = useSelector(selectIsSuperAdmin());
  const roleResolved = useSelector(selectRoleResolved());

  // Session hasn't resolved the role yet — wait rather than redirect, so a
  // logged-in super_admin who lands directly on /admin (deep link, refresh)
  // isn't ejected before we know who they are. Only relevant when the flag is
  // on; a flag-off user should redirect immediately regardless of role.
  if (superadminEnabled && !roleResolved) {
    return (
      <Wrapper>
        <div data-testid="admin-loading">
          <Loader />
        </div>
      </Wrapper>
    );
  }

  // Flag off, or a resolved non-super_admin → this surface doesn't exist for
  // them. Bounce to the default authed landing.
  if (!superadminEnabled || !isSuperAdmin) {
    return <Navigate to="/channels" replace />;
  }

  return (
    <Wrapper>
      <HelmetProvider>
        <Helmet title="Movebooth | Admin" />
      </HelmetProvider>
      <div className="admin-page__content">
        {/* Nested admin routes render here. /admin/users/new and
            /admin/users/:id overlap; under v7 the literal `new` wins on rank
            (static segment beats dynamic), so no ordering construct is needed
            where v5 required a <Switch>. */}
        <Outlet />
      </div>
    </Wrapper>
  );
};

export default AdminPage;
